import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import LockIcon from "@material-ui/icons/Lock";
import classnames from "classnames";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { UNDEFINED_LOCK_OPTION } from "entities/charts";
import { updateChartByPath } from "store/features";
import { LockState } from "types/charts/locks";

import { useChartEntities } from "hooks/charts";

import { DataEnum, Format } from "models";

import { AxisInput } from "./AxisInput";
import "./AxisLocker.scss";
import { LockIconWrapper } from "./AxisLocker.styles";

export function XMinLocker({ onInputStateChange, xAxisIndex = 0 }) {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const {
    options: optionString,
    locks,
    mpcLocks,
    multiProduct,
    chartType,
    timeStep,
    apiResponse
  } = useChartEntities(id);

  const isMultiProductActive = multiProduct?.active;

  // Keep a ref to mpcLocks so the options-sync effect always reads the latest value
  // without adding mpcLocks to its dep array (which would cause a dispatch loop when
  // override === true: dispatch → mpcLocks changes → effect re-runs → dispatch again).
  const mpcLocksRef = useRef(mpcLocks);
  mpcLocksRef.current = mpcLocks;

  const parsedAxis = useMemo(() => {
    const defaultReturn = {
      xAxisData: undefined,
      lockOptions: UNDEFINED_LOCK_OPTION,
      productKey: undefined
    };
    if (optionString.length === 0) return defaultReturn;
    try {
      const options = JSON.parse(optionString);
      const xAxisData = Array.isArray(options.xAxis)
        ? options.xAxis[xAxisIndex]
        : options.xAxis;
      const lockOptions = xAxisData?.lock ?? UNDEFINED_LOCK_OPTION;
      return {
        xAxisData,
        lockOptions,
        productKey: lockOptions?.productKey as string | undefined
      };
    } catch {
      return defaultReturn;
    }
  }, [optionString, xAxisIndex]);

  const [showLock, setShowLock] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [xMin, setXMin] = useState<LockState>(undefined);
  const [showInput, setShowInput] = useState(false);

  const timeCharts = [
    EvaChart.RateDate,
    EvaChart.RateDateMidstream,
    EvaChart.TotalRateDate,
    EvaChart.TotalRateDateMidstream,
    EvaChart.CAGR,
    EvaChart.DeclineRate,
    EvaChart.InjectorTotal
  ];
  const numberDateCharts = [EvaChart.RateTime, EvaChart.CumTime];
  const isNotTimeChart = !timeCharts.includes(chartType);
  const isNumberTimeChart = numberDateCharts.includes(chartType);

  useEffect(() => {
    onInputStateChange?.(showInput && isNotTimeChart);
  }, [showInput, onInputStateChange]);

  useEffect(() => {
    if (optionString.length === 0) return;
    const { xAxisData, lockOptions, productKey } = parsedAxis;

    setShowLock(lockOptions.show);
    setXMin(lockOptions.min);
    const override = lockOptions?.min?.override ?? false;

    if (override && xAxisData?.min !== undefined) {
      if (isMultiProductActive && productKey) {
        const existingMin = mpcLocksRef.current?.x?.[productKey]?.min;
        if (existingMin?.type !== DataEnum.Reset) {
          dispatch(
            updateChartByPath({
              id,
              path: `mpcLocks.x.${productKey}.min`,
              value: { value: xAxisData.min, type: lockOptions.min.type }
            })
          );
        }
      } else if (locks?.xMin?.type !== DataEnum.Reset) {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.xMin`,
            value: { value: xAxisData.min, type: lockOptions.min.type }
          })
        );
      }
    }
  }, [optionString, xAxisIndex, isMultiProductActive]);

  useEffect(() => {
    if (isMultiProductActive && parsedAxis.productKey) {
      const axisMinLock = mpcLocks?.x?.[parsedAxis.productKey]?.min;
      setIsLocked(axisMinLock?.value !== undefined);
    } else if (locks.xMin) {
      // A saved default the current data doesn't reach is not on the axis; showing it
      // locked would make the click store Reset and turn the default off for good
      const isUnappliedDefault =
        locks.xMin.type === DataEnum.DefaultDate && parsedAxis.xAxisData?.min == null;
      setIsLocked(locks.xMin.value !== undefined && !isUnappliedDefault);
    }
  }, [locks, mpcLocks, isMultiProductActive, parsedAxis]);

  const handleLockClick = () => {
    if (isLocked) {
      if (isMultiProductActive && parsedAxis.productKey) {
        dispatch(
          updateChartByPath({
            id,
            path: `mpcLocks.x.${parsedAxis.productKey}.min`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      } else {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.xMin`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      }
    } else {
      setShowInput(!showInput);
    }
  };

  const axisInputClassnames = classnames({ xAxis: true });
  const lockClassnames = classnames("axis-locker", "flex", {
    isLocked,
    xAxis: true,
    min: true
  });

  // Get series from apiResponse if available
  const series = apiResponse?.series || [];

  return (
    <>
      {showInput ? (
        <AxisInput
          axisInput={xMin}
          dataRange="xMin"
          lockPath={
            isMultiProductActive && parsedAxis.productKey
              ? `mpcLocks.x.${parsedAxis.productKey}.min`
              : undefined
          }
          setShowInput={setShowInput}
          className={classnames(axisInputClassnames, lockClassnames)}
          inputFormat={xMin?.format ?? Format.Number}
          chartType={chartType}
          series={series}
          unit={
            isNumberTimeChart
              ? timeStep?.properties?.value === TimeStep.Month ||
                timeStep?.properties?.value === TimeStep.Hybrid
                ? "months"
                : "days"
              : ""
          }
        />
      ) : (
        showLock && (
          <LockIconWrapper
            className={lockClassnames}
            onClick={handleLockClick}
            data-testid="x-min-locker">
            <LockIcon />
          </LockIconWrapper>
        )
      )}
    </>
  );
}
